import { ShaderMaterial } from "three";

const vertexShader = /* glsl */ `
in vec3 aCenter;
in vec4 aSpin;
in float aSize;
in vec4 aColor;
// Fase 14: a escala por eixo antes do giro (o fio da faísca, a lâmina do cristal) e, em w, o cristal (1).
in vec4 aShape;
in vec3 aBary;
out vec4 vColor;
out vec3 vView;
out vec3 vBary;
out float vCrystal;

// Gira \`p\` em volta do eixo (unitário) pelo ângulo (Rodrigues).
vec3 spin(vec3 p, vec3 axis, float angle) {
  float c = cos(angle);
  float s = sin(angle);
  return p * c + cross(axis, p) * s + axis * dot(axis, p) * (1.0 - c);
}

void main() {
  vColor = aColor;
  vBary = aBary;
  vCrystal = aShape.w;
  // O cristal achata pelo eixo de um canto (1,1,1): a lâmina sai um triângulo com o canto no meio (as três
  // facetas de dentro), não o quadrado que o achatamento por um eixo do tetraedro daria.
  const mat3 CORNER_UP = mat3(0.788675, -0.211325, 0.577350, -0.211325, 0.788675, 0.577350, -0.577350, -0.577350, 0.577350);
  vec3 base = aShape.w > 0.5 ? CORNER_UP * position : position;
  vec3 local = spin(base * aShape.xyz * aSize, aSpin.xyz, aSpin.w);
  vec4 view = viewMatrix * vec4(aCenter + local, 1.0);
  vView = view.xyz;
  gl_Position = projectionMatrix * view;
}
`;

const fragmentShader = /* glsl */ `
uniform float uGlow;
in vec4 vColor;
in vec3 vView;
in vec3 vBary;
in float vCrystal;

void main() {
  // Faces planas (normal da derivada): o estilhaço low-poly lê pelas facetas, mais claras de frente.
  vec3 n = normalize(cross(dFdx(vView), dFdy(vView)));
  float facet = 0.55 + 0.45 * abs(n.z);
  if (vCrystal > 0.5) {
    // Cristal (Fase 14, a referência): vidro quase transparente na cor de quem bateu, as arestas acesas
    // (largura constante na tela, pela baricêntrica) e um brilho na face de frente.
    vec3 d = max(fwidth(vBary), vec3(1e-4));
    vec3 e = smoothstep(vec3(0.0), d * 1.4, vBary);
    float edge = 1.0 - min(min(e.x, e.y), e.z);
    float sheen = pow(abs(n.z), 6.0);
    float alpha = max(edge, 0.22 + 0.25 * sheen) * vColor.a;
    gl_FragColor = vec4(vColor.rgb * mix(facet * 0.8 + sheen, uGlow * 1.25, edge), alpha);
    #include <colorspace_fragment>
    return;
  }
  gl_FragColor = vec4(vColor.rgb * facet * uGlow, vColor.a);
  #include <colorspace_fragment>
}
`;

/** Brilho dos estilhaços: a cor de quem causou passa de 1,0 e o bloom pega o impacto. */
export const SHARD_GLOW = 2.2;

/**
 * Estilhaços (F68, guia §21): tetraedros instanciados num único draw, na cor de quem causou. A cor passa
 * de 1,0 (\`uGlow\`), então o bloom pega o impacto; no Baixo (sem pós), ficam só as facetas. O entulho e
 * a camada ambiente (F90) usam o mesmo draw e o mesmo programa, com a cor já dividida por \`SHARD_GLOW\`
 * para ficar no cinza (ou só um pouco acima de 1, nos vagalumes).
 */
export function createShardsMaterial(): ShaderMaterial {
  return new ShaderMaterial({ name: "estilhacos", uniforms: { uGlow: { value: SHARD_GLOW } }, vertexShader, fragmentShader, transparent: true, depthWrite: false });
}
